import { EventEmitter, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { environment } from '../environments/environment';
import * as moment from 'moment';
import { SiteConfig } from './interfaces/SiteConfig';
import { Config } from './interfaces/Config';

@Injectable()
export class SharedService {
  private config: Config;
  private window;
  private validSteps = { step1: false, step2: false, step3: false };
  private storageName;
  private env;
  private isLoggedIn;

  loggedIn: EventEmitter<boolean> = new EventEmitter();

  constructor(private cookieService: CookieService) {
    /*
     * Get configuration from
     * global, external config file via
     * window.
     * Configuration object is added to window
     * as property.
     */
    this.window = window;
    this.config = window[environment.configName];
    this.env = environment.configName;

    this.storageName = this.getSiteConfig().showCartPage
      ? this.getSiteConfig().storageName + this.getLotterySource()
      : this.getSiteConfig().storageName + '-landing';
  }

  getEnvironment(): string {
    return this.env === 'nklConfig' ? 'nkl' : 'skl';
  }

  getCookie(key: string) {
    return this.cookieService.get(key);
  }

  getWindow() {
    return this.window;
  }

  getConfig(): Config {
    if (!this.config) {
      console.warn(
        'WARNING: There is no siteConfig set! Please set a siteConfig object as described in the documentation!'
      );
    }
    return this.config;
  }

  getSiteConfig(): SiteConfig {
    if (!this.config) {
      console.warn(
        'WARNING: There is no siteConfig set! Please set a siteConfig object as described in the documentation!'
      );
    }
    return this.config.siteConfig;
  }

  getLotterySource() {
    return this.window.lotterySrc;
  }

  /**
   * Returns the jokerType which is currently
   * set via site config
   * jokerType was added to siteConfig to enable a
   * christmas joker with exactly the same product as dream joker
   * but only different wording and background images.
   */
  getJokerType(): string {
    return this.config.siteConfig.jokerType;
  }

  checkLocalStorage(storage) {
    return localStorage.getItem(this.storageName + '-' + storage);
  }

  getLocalStorage(storage) {
    return localStorage.getItem(this.storageName + '-' + storage)
      ? JSON.parse(localStorage.getItem(this.storageName + '-' + storage))
      : {};
  }

  getSessionStorage(storage) {
    return sessionStorage.getItem(this.storageName + '-' + storage)
      ? JSON.parse(sessionStorage.getItem(this.storageName + '-' + storage))
      : {};
  }

  removeFromLocalStorage(storage) {
    if (localStorage.getItem(this.storageName + '-' + storage)) {
      localStorage.removeItem(this.storageName + '-' + storage);
    }
  }

  removeFromSessionStorage(storage) {
    if (sessionStorage.getItem(this.storageName + '-' + storage)) {
      sessionStorage.removeItem(this.storageName + '-' + storage);
    }
  }

  clearOrderDataFromLs() {
    localStorage.removeItem(this.storageName + '-' + 'lotteryData');
    localStorage.removeItem(this.storageName + '-' + 'addressData');
    localStorage.removeItem(this.storageName + '-' + 'prefilled');
  }

  /*
   * @param {String} name
   * @return {Array} parameter
   *
   * Method to read out GET params from URL
   * It can read multiple url params with the same name and returns them
   * as an array
   */
  getUrlParamByName(name) {
    const sURL = window.document.URL.toString();
    const allParams = [];
    if (sURL.indexOf('?') > 0) {
      const arrParams = sURL.split('?');
      const arrURLParams = arrParams[1].split('&');
      for (let i = 0; i < arrURLParams.length; i++) {
        const sParam = arrURLParams[i].split('=');
        if (sParam) {
          if (sParam[0] === name) {
            if (sParam.length > 0) {
              allParams.push(sParam[1].trim());
            }
          }
        }
      }
    }
    return allParams;
  }

  setCookie(key: string, value, expireDays: number = 30) {
    const in30Days = moment().add(expireDays, 'days').toDate();
    const options = {
      expires: in30Days,
      domain: this.getDomain(),
      path: '/'
    };
    this.cookieService.put(key, value, options);
  }

  setLocalStorage(storage, data) {
    const storageName =
      storage === 'orderResponse' ? storage : this.storageName + '-' + storage;
    localStorage.setItem(storageName, JSON.stringify(data));
  }

  setSessionStorage(storage, data) {
    const storageName = this.storageName + '-' + storage;
    sessionStorage.setItem(storageName, JSON.stringify(data));
  }

  getDomain() {
    const hostName = window.location.hostname;
    return hostName.substring(
      hostName.lastIndexOf('.', hostName.lastIndexOf('.') - 1) + 1
    );
  }

  removeCookie(key: string) {
    this.cookieService.remove(key);
  }

  validateStep(step) {
    this.validSteps[step] = true;
  }

  invalidStep(step) {
    this.validSteps[step] = false;
  }

  isValidStep(step) {
    return this.validSteps[step];
  }

  generateRandomHash() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  pushToDataLayer(action, products) {
    const gtmData = {
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: action,
          products: products
        }
      }
    };

    this.window.dataLayer.push(gtmData);
  }

  pushErrorToDataLayer(error, submitId?) {
    const errorData = {
      event: 'error',
      category: 'error',
      action: 'ORBIT',
      label: error,
      ...(submitId && { submitId })
    };
    this.window.dataLayer.push(errorData);
  }

  pushCustomToDataLayer(data, clearBefore = true) {
    if (clearBefore) {
      this.window.dataLayer.push({ ecommerce: null });
    }
    this.window.dataLayer.push(data);
  }

  setLoggedIn(isLoggedIn: boolean) {
    this.isLoggedIn = isLoggedIn;
    this.loggedIn.emit(this.isLoggedIn);
  }

  triggerInitEvent() {
    const event = new Event('bestellstreckeInit');

    document.dispatchEvent(event);
  }
}
